// The `?code=` → JWT handoff against the broker's `/auth/exchange`. Errors are
// returned as values (never thrown) with a distinguishing `reason`, so the
// handler can log the specific failure case BEFORE it redirects (guardrail 3).
// `fetch` is the only side-effect, injectable for tests.

import { DEFAULT_LANES, isSessionLane, type SessionLane } from "./verify";

/**
 * On success, `jwtType` is the type the broker REPORTED for the token, when
 * it reported one — a hint for the caller's logs, not a verdict. The
 * authority on which lane a session is in is `verifyBrokerJwt`'s `lane`,
 * read from the signed `typ` claim; every caller here verifies next.
 */
export type ExchangeResult =
  | { ok: true; jwt: string; jwtType?: SessionLane }
  | { ok: false; reason: string };

export interface ExchangeParams {
  brokerOrigin: string;
  code: string;
  /** Hostname binding — must equal the audience the code was minted against. */
  audience: string;
  fetchImpl?: typeof fetch;
  /**
   * The token types this exchange accepts from the broker's `jwtType`.
   * Omitted, `user_session` only — what every consumer accepted before the
   * reader lane existed.
   */
  lanes?: readonly SessionLane[];
  /** Defaults to {@link EXCHANGE_TIMEOUT_MS}. */
  timeoutMs?: number;
}

/** The broker's exchange is two Convex calls; a hung one must not hang us. */
export const EXCHANGE_TIMEOUT_MS = 15_000;

/**
 * POST `{ code, audience }` to `${brokerOrigin}/auth/exchange`. No client secret
 * — the single-use, 60s-TTL, audience-bound code is the bearer. Returns the raw
 * JWT on success; a typed failure otherwise (network, non-2xx, bad body, or a
 * reported token type outside `params.lanes`, `user_session` by default).
 */
export async function exchangeCode(
  params: ExchangeParams,
): Promise<ExchangeResult> {
  const doFetch = params.fetchImpl ?? fetch;
  let res: Response;
  try {
    res = await doFetch(`${params.brokerOrigin}/auth/exchange`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: params.code, audience: params.audience }),
      signal: AbortSignal.timeout(params.timeoutMs ?? EXCHANGE_TIMEOUT_MS),
    });
  } catch {
    return { ok: false, reason: "network" };
  }

  if (!res.ok) return { ok: false, reason: `status_${res.status}` };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, reason: "bad_json" };
  }

  if (typeof body !== "object" || body === null) {
    return { ok: false, reason: "bad_body" };
  }
  const record = body as Record<string, unknown>;
  if (typeof record.jwt !== "string" || record.jwt.length === 0) {
    return { ok: false, reason: "no_jwt" };
  }
  // The broker returns `jwtType`; a consumer accepts only the lanes it
  // declared. An absent `jwtType` passes, as it always has — the signed `typ`
  // the verifier reads next is the check that cannot be skipped.
  if (record.jwtType === undefined) return { ok: true, jwt: record.jwt };
  const lanes: readonly SessionLane[] = params.lanes ?? DEFAULT_LANES;
  if (!isSessionLane(record.jwtType) || !lanes.includes(record.jwtType)) {
    return { ok: false, reason: "wrong_type" };
  }
  return { ok: true, jwt: record.jwt, jwtType: record.jwtType };
}
